import * as DialogPrimitive from "@radix-ui/react-dialog";
import { FC } from "react";
import { Dialog, DialogClose, DialogOverlay, DialogPortal } from "./Dialog";
import { MODAL_MOTION_CLASSNAME } from "./dialogShell";
import { cn } from "@src/lib/utils/cn";
import XIcon from "@src/lib/icons/12px/x";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string | null | undefined;
};

/**
 * Full-screen image preview dialog. Uses a raw `DialogPrimitive.Content`
 * (instead of the shared `DialogContent`) so it can escape the z-index of the
 * dialog that opened it.
 */
const ImagePreviewDialog: FC<Props> = ({ open, onOpenChange, imageUrl }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogPortal>
      {/* Full-screen photo preview, often over another dialog: keep a darker
          scrim than the 24% modal scrim so the image stands out. */}
      <DialogOverlay className="z-[9998] bg-black/60" />
      <DialogPrimitive.Content
        data-testid="preview-dialog-content"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "fixed top-1/2 left-1/2 z-[9999] grid w-max max-w-[90vw] min-w-[240px] min-h-[240px] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-2xl border border-border-primary bg-bg-elevated p-0 shadow-extra-large outline-none origin-center will-change-[transform,opacity]",
          MODAL_MOTION_CLASSNAME,
          "data-[state=open]:zoom-in-[var(--modal-scale)] data-[state=closed]:zoom-out-[var(--modal-scale-close)]",
        )}
      >
        <DialogPrimitive.Title className="sr-only">
          Image preview
        </DialogPrimitive.Title>
        <div className="relative flex items-center justify-center p-4">
          <img
            src={imageUrl || ""}
            alt="Preview"
            className="max-h-[85vh] max-w-full rounded-lg object-contain"
          />
        </div>
        {/* Sits on the image's corner, so it carries its own surface. */}
        <DialogClose className="absolute top-6 right-6 flex size-8 items-center justify-center rounded-full bg-bg-elevated text-icon-secondary shadow-default transition-colors outline-none hover:bg-bg-secondary hover:text-icon-primary focus-visible:ring-1 focus-visible:ring-brand-border disabled:pointer-events-none">
          <XIcon className="size-3" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogPrimitive.Content>
    </DialogPortal>
  </Dialog>
);

export default ImagePreviewDialog;
